import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { Path } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'

import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/common/api/pagination'
import { makeParamsQueryStr } from '@/common/api/rest'
import { AssociatedArray } from '@/common/util/array'
import type { SearchConditionBase } from '@/common/util/search'

type SearchConditionType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'array'
  | 'numberArray'
  | 'booleanArray'

export interface SearchConditionParam {
  name: string
  type: SearchConditionType
}

// SearchConditionBase の実体は common/util/search.ts（view 層から参照できるよう移設した）
export type { SearchConditionBase }

const SEARCH_CONDITION_BASE: SearchConditionParam[] = [
  { name: 'page', type: 'number' },
  { name: 'perPage', type: 'number' }
]

const convertQueryParamToSearchConditionValue = (
  param: string | null,
  type: SearchConditionType
) => {
  switch (type) {
    case 'number':
      return param != null ? Number(param) : null
    case 'string':
      return param
    case 'boolean':
      return param != null ? !!Number(param) : null
    case 'array':
      return param ? param.split(',') : []
    case 'numberArray':
      return param ? param.split(',').map((value) => Number(value)) : []
    case 'booleanArray':
      return param ? param.split(',').map((value) => !!Number(value)) : []
  }
}

const filterDefaultParams = <T extends SearchConditionBase>(condition: T) => {
  let params: AssociatedArray = {}
  Object.keys(condition).forEach((key) => {
    const value = condition[key as Path<T>]
    if (
      !(
        (key == 'page' && condition[key] == DEFAULT_PAGE) ||
        (key == 'perPage' && condition[key] == DEFAULT_PER_PAGE)
      ) &&
      value != undefined &&
      !(Array.isArray(value) && value.length == 0)
    ) {
      params[key] = value
    }
  })
  return params
}

const convertSearchParamsToCondition = <T extends SearchConditionBase>(
  searchParams: URLSearchParams,
  params: SearchConditionParam[],
  convert?: (paramValues: T) => T
): T => {
  const paramValues: AssociatedArray = {}
  ;[...SEARCH_CONDITION_BASE, ...params].forEach((param) => {
    const value = searchParams.get(param.name)
    if (param.name == 'page' && !value) {
      paramValues[param.name] = DEFAULT_PAGE
    } else if (param.name == 'perPage' && !value) {
      paramValues[param.name] = DEFAULT_PER_PAGE
    } else {
      paramValues[param.name] = convertQueryParamToSearchConditionValue(value, param.type)
    }
  })

  return (convert ? convert(paramValues as T) : paramValues) as T
}

export const useSearchCondition = <T extends SearchConditionBase>(
  params: SearchConditionParam[],
  convert?: (paramValues: T) => T
): [T, Dispatch<SetStateAction<T>>, (page: number) => void] => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [condition, setCondition] = useState<T>(
    convertSearchParamsToCondition(searchParams, params, convert)
  )

  useEffect(() => {
    setCondition(convertSearchParamsToCondition(searchParams, params, convert))
  }, [searchParams])

  useEffect(() => {
    const newSearchParams = condition ? makeParamsQueryStr(filterDefaultParams(condition)) : ''
    const path = location.pathname + newSearchParams
    if (path == location.pathname + location.search) {
      return
    }
    setSearchParams(newSearchParams, { replace: true })
  }, [condition])

  const onUpdatePage = useCallback(
    (page: number) => {
      setCondition((condition) => {
        return {
          ...condition,
          page
        }
      })
    },
    [setCondition, condition]
  )

  return [condition, setCondition, onUpdatePage]
}

export const isSameSearchCondition = <T extends SearchConditionBase>(
  conditionA: T,
  conditionB: T
) => {
  return JSON.stringify(conditionA) == JSON.stringify(conditionB)
}

export const useFirstLoading = (isLoading: boolean): boolean => {
  const [isFirstLoading, setIsFirstLoading] = useState<boolean>(true)

  useEffect(() => {
    !isLoading && setIsFirstLoading(false)
  }, [!isFirstLoading || isLoading])

  return isFirstLoading && isLoading
}

export const shapeKeywordWhereInput = <T extends { OR?: AssociatedArray[] }>(
  keyword: string,
  shapeEachWordWhereInput: (keyword: string) => AssociatedArray
): T => {
  const keywords = keyword
    .split(/( |　)/)
    .filter((word) => word.length > 0 && word != ' ' && word != '　')

  return {
    OR: [...keywords.map((word) => shapeEachWordWhereInput(word))]
  } as T
}

// for int
export const shapeSerialNumberWhereInput = (text: string): number => {
  return Number(text.substring(0, 8))
}
